import React, { useState, useEffect } from 'react';
import {
  Modal, Box, Typography, Button, Grid, TextField, CircularProgress, MenuItem, Select,
  FormControl, InputLabel, FormHelperText, RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import { toast } from 'react-toastify';
import { createLab, getLabType, updateLab } from "../../../services/mentorServices/MentorServices";
import { getAllMentors } from '../../../services/adminServices/AdminServices';

const convertTo24HourFormat = (time = '12') => {
  const [hour, minuteAndPeriod] = time?.split(':');
  const [minute, period] = minuteAndPeriod?.split(' ');
  let hour24 = parseInt(hour);
  if (period === 'PM' && hour24 !== 12) hour24 += 12;
  if (period === 'AM' && hour24 === 12) hour24 = 0;
  return `${hour24.toString().padStart(2, '0')}:${minute}`;
};

const convertTo12HourFormat = (time = '12') => {
  let [hour, minute] = time?.split(':');
  hour = parseInt(hour);
  const period = hour >= 12 ? 'PM' : 'AM';
  const newHour = hour % 12 || 12;
  return `${newHour.toString().padStart(2, '0')}:${minute.padStart(2, '0')} ${period}`;
};

const validateTimeDifference = (start, end) => {
  const startDate = new Date(`1970-01-01T${start}:00`);
  const endDate = new Date(`1970-01-01T${end}:00`);
  return (endDate - startDate) >= 30 * 60 * 1000;
};

const INITIAL_FORM_STATE = {
  COE: '', labName: '', startTime: '', endTime: '', semester: 0, labType: '',
  mainMentor: '', supportMentors: [''], enrolment_year: ''
};
const INITIAL_ERRORS_STATE = {};

const LabPracticeModal = ({ open, onClose, editRowData, getAllLabsData, allCOE, user }) => {
  const [formValues, setFormValues] = useState(INITIAL_FORM_STATE);
  const [formErrors, setFormErrors] = useState(INITIAL_ERRORS_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [labTypes, setLabTypes] = useState([]);
  const [mentors, setMentors] = useState([]);

  useEffect(() => {
    setFormValues(INITIAL_FORM_STATE);
    setFormErrors(INITIAL_ERRORS_STATE);
  }, [open]);

  useEffect(() => {
    getLabType()
      .then(response => setLabTypes(response?.data?.data.value || []))
      .catch(error => toast.error(error?.response?.data.message || error?.message || error?.error));
  }, []);

  const getAllUsersData = async () => {
    try {
      const response = await getAllMentors();
      setMentors(response?.data?.data);
    } catch (error) {
      toast.error(error?.response?.data.message || error?.message || error?.error);
    }
  };

  useEffect(() => {
    getAllUsersData();
  }, []);

  useEffect(() => {
    if (editRowData) {
      setFormValues({
        COE: editRowData.location_id || '',
        labName: editRowData.lab_name || '',
        startTime: convertTo24HourFormat(editRowData.startTime) || '',
        endTime: convertTo24HourFormat(editRowData.endTime) || '',
        semester: editRowData.semester || 0,
        enrolment_year: editRowData.enrolment_year || '',
        labType: editRowData.lab_type || '',
        mainMentor: editRowData.main_mentor?.id || '',
        supportMentors: Array.isArray(editRowData.support_mentors)
          ? (editRowData.support_mentors.length > 0
            ? editRowData.support_mentors.map(m => m.id)
            : [''])
          : [''],
      });
    } else {
      setFormValues(INITIAL_FORM_STATE);
      setFormErrors(INITIAL_ERRORS_STATE);
    }
  }, [editRowData]);

  const handleInputChange = (e, index = null) => {
    const { name, value } = e.target;
    if (name === 'supportMentor' && index !== null) {
      const updated = [...formValues.supportMentors];
      updated[index] = parseInt(value);
      setFormValues(prev => ({ ...prev, supportMentors: updated }));
    } else {
      setFormValues(prev => ({ ...prev, [name]: value }));
    }
  };

  const addSupportMentorField = () => {
    setFormValues(prev => ({
      ...prev,
      supportMentors: [...prev.supportMentors, '']
    }));
  };

  const removeSupportMentorField = (index) => {
    setFormValues(prev => {
      const updated = [...prev.supportMentors];
      updated.splice(index, 1);
      return { ...prev, supportMentors: updated };
    });
  };

  const validateFields = () => {
    const errors = {};
    const { COE, labName, startTime, endTime, semester, labType, mainMentor, enrolment_year } = formValues;

    if (!COE) errors.COE = 'COE is required*';
    if (!labName) errors.labName = 'Lab Name is required*';
    if (!startTime) errors.startTime = 'Start Time is required*';
    if (!endTime) errors.endTime = 'End Time is required*';
    if (startTime && endTime && !validateTimeDifference(startTime, endTime)) {
      errors.endTime = 'End Time must be at least 30 minutes after Start Time';
    }
    if (!/^[1-8]$/.test(semester)) {
      errors.semester = 'Semester must be a number between 1 and 8';
    }
    if ( !/^\d{4}$/.test(enrolment_year)) {
      errors.enrolment_year = 'Enter a valid 4-digit enrolment year (e.g., 2021)';
    }
    if (!labType) errors.labType = 'Lab Type is required*';
    if (!mainMentor) errors.mainMentor = 'Main Mentor is required*';

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = validateFields();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);
 const basePayload = {
    lab_name: formValues.labName,
    location_id: formValues.COE,
    startTime: convertTo12HourFormat(formValues.startTime),
    endTime: convertTo12HourFormat(formValues.endTime),
    semester: parseInt(formValues.semester, 10),
    enrolment_year: parseInt(formValues.enrolment_year, 10) || 0,
    lab_type: formValues.labType,
    support_mentor_ids: formValues.supportMentors.filter(Boolean)
  };

  // Merge with mentor field depending on create/update
  const payload = editRowData
    ? { ...basePayload, mentor_id: parseInt(formValues.mainMentor, 10) }
    : { ...basePayload, mentor_ids: parseInt(formValues.mainMentor, 10) };
    try {
      const response = editRowData
        ? await updateLab(editRowData.id, payload)
        : await createLab(payload);
      setFormValues(INITIAL_FORM_STATE);
      toast.success(response.data.message);
      getAllLabsData();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data.message || error?.message || error?.error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAvailableMentors = (currentId) => {
    return mentors.filter(m =>
      m.id !== formValues.mainMentor &&
      (!formValues.supportMentors.includes(m.id) || m.id === currentId)
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '45%', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, maxHeight: '95vh', overflowY: 'auto', }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {editRowData ? "Edit Lab " : "Create Lab "}
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* COE */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={!!formErrors.COE}>
                <InputLabel>COE*</InputLabel>
                <Select value={formValues.COE} onChange={handleInputChange} name="COE" label="COE">
                  {allCOE?.map((data) => (
                    <MenuItem key={data.id} value={data.id}>{data.name}</MenuItem>
                  ))}
                </Select>
                {formErrors.COE && <FormHelperText>{formErrors.COE}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Lab Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="text"
                name="labName"
                value={formValues.labName}
                onChange={handleInputChange}
                error={!!formErrors.labName}
                helperText={formErrors.labName}
                label="Lab Name *"
              />
            </Grid>

            {/* Start Time */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="time"
                name="startTime"
                value={formValues.startTime}
                onChange={handleInputChange}
                error={!!formErrors.startTime}
                helperText={formErrors.startTime}
                label="Start Time *"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* End Time */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="time"
                name="endTime"
                value={formValues.endTime}
                onChange={handleInputChange}
                error={!!formErrors.endTime}
                helperText={formErrors.endTime}
                label="End Time *"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Semester */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="text"
                name="semester"
                value={formValues.semester}
                onChange={handleInputChange}
                error={!!formErrors.semester}
                helperText={formErrors.semester}
                label="Semester *"
              />
            </Grid>

            {/* Enrolment Year */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="text"
                name="enrolment_year"
                value={formValues.enrolment_year}
                onChange={handleInputChange}
                error={!!formErrors.enrolment_year}
                helperText={formErrors.enrolment_year}
                label="Enrolment Year *"
              />
            </Grid>

            {/* Main Mentor */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={!!formErrors.mainMentor}>
                <InputLabel>Main Mentor*</InputLabel>
                <Select value={formValues.mainMentor} onChange={handleInputChange} name="mainMentor" label="Main Mentor*">
                  {mentors.map((mentor) => (
                    <MenuItem key={mentor.id} value={mentor.id}>{mentor.email}</MenuItem>
                  ))}
                </Select>
                {formErrors.mainMentor && <FormHelperText>{formErrors.mainMentor}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Support Mentors */}
            <Grid item xs={12}>
              <Typography variant="subtitle1">Support Mentors</Typography>
              {formValues.supportMentors.map((mentorId, index) => (
                <Box key={`support-mentor-${index}`} display="flex" alignItems="center" gap={1} mt={1}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Support Mentor</InputLabel>
                    <Select
                      name="supportMentor"
                      value={mentorId}
                      onChange={(e) => handleInputChange(e, index)}
                      label="Support Mentor"
                    >
                      {getAvailableMentors(mentorId).map(m => (
                        <MenuItem key={m.id} value={m.id}>{m.email}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {index === formValues.supportMentors.length - 1 && (
                    <Button onClick={addSupportMentorField}>➕</Button>
                  )}
                  {formValues.supportMentors.length > 1 && (
                    <Button onClick={() => removeSupportMentorField(index)}>➖</Button>
                  )}
                </Box>
              ))}
            </Grid>

            {/* Lab Type */}
            <Grid item xs={12}>
              <FormControl component="fieldset" error={!!formErrors.labType} sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center" }}>
                <Typography variant="body1" mr={3}>Lab Type *</Typography>
                <RadioGroup sx={{ justifyContent: "space-evenly" }} row name="labType" value={formValues.labType} onChange={handleInputChange}>
                  {labTypes?.map((labType) => (
                    <FormControlLabel key={labType} value={labType} control={<Radio />} label={labType} />
                  ))}
                </RadioGroup>
              </FormControl>
              <FormHelperText sx={{ color: "#d32f2f", paddingLeft: "15px" }}>{formErrors.labType}</FormHelperText>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Button type="submit" variant="contained" color="primary" disabled={isLoading} sx={{ marginRight: 2 }}>
                {isLoading ? <CircularProgress size={24} /> : editRowData ? "Update" : "Create"}
              </Button>
              <Button variant="outlined" color="secondary" onClick={onClose}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Modal>
  );
};

export default LabPracticeModal;
